import React from "react";
import { Brain, ShoppingCart, Flame, Zap, Bot, BadgeDollarSign } from "lucide-react";

const CAPABILITIES = [
  {
    icon: <Brain className="w-5 h-5" />,
    title: "Turn Your Expertise Into Products",
    desc: "Package your knowledge into high-ticket services, courses, memberships or digital products.",
  },
  {
    icon: <ShoppingCart className="w-5 h-5" />,
    title: "Launch Your AI Storefront",
    desc: "List your offers, consultations, payment links and booking system \u2014 all in one place.",
  },
  {
    icon: <Flame className="w-5 h-5" />,
    title: "Build an Unstoppable Personal Brand",
    desc: "Discover viral opportunities and create content that attracts your ideal customers.",
  },
  {
    icon: <Zap className="w-5 h-5" />,
    title: "Produce Content 10\u00d7 Faster",
    desc: "Generate hooks, scripts, videos, carousels and creatives with AI \u2014 without hiring a team.",
  },
  {
    icon: <Bot className="w-5 h-5" />,
    title: "Automate Client Acquisition",
    desc: "Capture leads, automate DMs, nurture prospects and book sales calls on autopilot.",
  },
  {
    icon: <BadgeDollarSign className="w-5 h-5" />,
    title: "Scale to Predictable Revenue",
    desc: "Turn your audience into paying clients and build an AI-powered business that grows every month.",
  },
];

export default function ViralXDemo() {
  return (
    <section className="relative z-10 px-4 py-10 md:py-14">
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-[#C4FF3D] uppercase tracking-[0.28em] text-[11px] md:text-xs font-bold mb-3">
          Powered By ViralX
        </p>
        <h2 className="display-font text-center text-3xl md:text-5xl font-extrabold leading-tight mb-4">
          <span className="text-white">Meet the</span>{" "}
          <span className="lime-box">AI Operating System</span>{" "}
          <span className="text-white">Behind the Content Engine.</span>
        </h2>
        <p className="text-center text-white/65 max-w-3xl mx-auto mb-14">
          See how ViralX helps experts identify winning content opportunities, analyse high-performing formats, accelerate ideation and build repeatable content workflows.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Reel-format video (portrait 9:16) */}
          <div className="relative min-w-0 flex justify-center">
            <div className="absolute -inset-10 bg-[radial-gradient(circle,rgba(196,255,61,0.15),transparent_60%)] blur-3xl" />
            <div className="relative w-full max-w-[300px] md:max-w-[340px] aspect-[9/16] rounded-[32px] overflow-hidden border-2 border-[rgba(196,255,61,0.35)] bg-[#0a0f0a] shadow-[0_0_60px_rgba(196,255,61,0.15)]">
              <video
                src="https://customer-assets.emergentagent.com/job_viral-lp-preview/artifacts/h4lq88ha_GEMINI_VIDEO_GENERATION_PROMPT.mp4"
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="ViralX reel preview"
              />
              {/* Reel badge */}
              <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#0a0f0a]/85 border border-[rgba(196,255,61,0.35)] backdrop-blur-sm text-[#C4FF3D] text-[10px] uppercase tracking-widest font-bold">
                Reel
              </div>
              {/* Subtle bottom vignette */}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          </div>

          <div>
            <div className="space-y-4">
              {CAPABILITIES.map((c) => (
                <div key={c.title} className="flex items-start gap-4 p-4 md:p-5 rounded-2xl bg-[#0e1410] border border-[rgba(196,255,61,0.15)]">
                  <div className="w-10 h-10 shrink-0 rounded-xl bg-[#0a0f0a] border border-[rgba(196,255,61,0.3)] flex items-center justify-center text-[#C4FF3D]">
                    {c.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">{c.title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-white/45 text-xs mt-5 leading-relaxed">
              You'll see a live walkthrough of ViralX inside the workshop. Access to specific ViralX plans is separate from the workshop registration.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
